import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Loader2, Banknote, ListOrdered } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { trpc } from '@/lib/trpc';
import { formatCurrency, formatDate } from '@/lib/utils';

const PAYMENT_METHODS = ['CASH', 'CHECK', 'BANK_TRANSFER', 'CREDIT_CARD', 'DEBIT_CARD', 'PAYPAL', 'OTHER'] as const;

type PaymentMethod = (typeof PAYMENT_METHODS)[number];

interface OpenInvoiceRow {
  id: string;
  invoiceNumber: string;
  currency: string;
  balanceDue: number;
  dueDate: Date | string | null;
  invoiceDate: Date | string;
}

const round2 = (n: number) => Math.round(n * 100) / 100;

/** FIFO by due date within the payment currency, up to `amount`. */
export function fifoAllocation(amount: number, invoices: OpenInvoiceRow[], currency: string): Record<string, string> {
  let left = round2(amount);
  const next: Record<string, string> = {};
  const open = invoices
    .filter((i) => i.currency.toUpperCase() === currency.toUpperCase() && i.balanceDue > 0)
    .sort((a, b) => new Date(a.dueDate ?? a.invoiceDate).getTime() - new Date(b.dueDate ?? b.invoiceDate).getTime());
  for (const invoice of open) {
    if (left <= 0) break;
    const take = round2(Math.min(left, invoice.balanceDue));
    next[invoice.id] = String(take);
    left = round2(left - take);
  }
  return next;
}

/**
 * Payment entry page. Reachable from "Record Payment" actions across the
 * finance module. Reads invoiceId / supplierInvoiceId (settle one invoice) or
 * customerId / supplierId (on-account receipt) from the query string, lists
 * the counterparty's open invoices in the payment currency and lets the
 * accountant split the amount over them; whatever is not allocated stays on
 * account and can be applied later from the payment page.
 */
export function NewPaymentPage() {
  const navigate = useNavigate();
  const { t: tCommon } = useTranslation('common');
  const [params] = useSearchParams();
  const invoiceId = params.get('invoiceId') ?? undefined;
  const supplierInvoiceId = params.get('supplierInvoiceId') ?? undefined;
  const customerIdParam = params.get('customerId') ?? undefined;
  const supplierIdParam = params.get('supplierId') ?? undefined;
  const isSupplier = !!supplierInvoiceId || (!!supplierIdParam && !invoiceId && !customerIdParam);
  const side = isSupplier ? 'SUPPLIER' : 'CUSTOMER';

  const customerInvoice = trpc.tradingInvoice.byId.useQuery(invoiceId!, { enabled: !!invoiceId });
  const supplierInvoice = trpc.tradingSupplierInvoice.byId.useQuery(supplierInvoiceId!, { enabled: !!supplierInvoiceId });

  const linked = isSupplier ? supplierInvoice.data : customerInvoice.data;
  const loadingLinked = (!!invoiceId && customerInvoice.isLoading) || (!!supplierInvoiceId && supplierInvoice.isLoading);
  const partyId = isSupplier
    ? (supplierIdParam ?? supplierInvoice.data?.supplierId ?? null)
    : (customerIdParam ?? customerInvoice.data?.tradingCustomerId ?? null);

  const openInvoices = trpc.tradingPayment.openInvoices.useQuery({ side, partyId: partyId ?? '' }, { enabled: !!partyId });

  const balanceDue = linked ? Number(linked.balanceDue) : 0;
  const linkedCurrency = linked?.currency ?? 'EUR';
  const linkedNumber = linked?.invoiceNumber ?? '';

  const [amount, setAmount] = useState<string>('');
  const [currency, setCurrency] = useState<string>('');
  const [method, setMethod] = useState<PaymentMethod>('BANK_TRANSFER');
  const [paymentDate, setPaymentDate] = useState<string>(new Date().toISOString().slice(0, 10));
  const [reference, setReference] = useState('');
  const [bankAccount, setBankAccount] = useState('');
  const [notes, setNotes] = useState('');
  const [touched, setTouched] = useState(false);
  const [allocations, setAllocations] = useState<Record<string, string>>({});
  const [allocTouched, setAllocTouched] = useState(false);

  // When the linked invoice arrives, seed amount/currency once.
  const effectiveAmount = touched ? amount : balanceDue > 0 ? String(balanceDue) : amount;
  const effectiveCurrency = currency || linkedCurrency;
  const numericAmount = Number(effectiveAmount);

  const invoices: OpenInvoiceRow[] = useMemo(() => (openInvoices.data ?? []).map((i) => ({ ...i, balanceDue: Number(i.balanceDue) })), [openInvoices.data]);
  const sameCurrency = invoices.filter((i) => i.currency.toUpperCase() === effectiveCurrency.toUpperCase());
  const otherCurrency = invoices.length - sameCurrency.length;

  // Until the accountant edits the split, the linked invoice takes as much as it can.
  const effectiveAllocations: Record<string, string> = allocTouched
    ? allocations
    : linked && numericAmount > 0
      ? { [linked.id]: String(round2(Math.min(numericAmount, balanceDue))) }
      : {};

  const allocationList = Object.entries(effectiveAllocations)
    .map(([id, v]) => ({ invoiceId: id, amount: Number(v) }))
    .filter((a) => Number.isFinite(a.amount) && a.amount > 0);
  const allocatedTotal = round2(allocationList.reduce((s, a) => s + a.amount, 0));
  const unallocated = round2(numericAmount - allocatedTotal);
  const overAllocatedRows = allocationList.filter((a) => {
    const inv = invoices.find((i) => i.id === a.invoiceId);
    return inv ? a.amount > inv.balanceDue + 0.005 : false;
  });
  const allocationsValid = unallocated >= -0.005 && overAllocatedRows.length === 0;

  const utils = trpc.useUtils();
  const onDone = () => {
    utils.tradingPayment.list.invalidate();
    navigate('/trading/finance/payments');
  };

  const customerMutation = trpc.tradingPayment.recordCustomerPayment.useMutation({ onSuccess: onDone });
  const supplierMutation = trpc.tradingPayment.recordSupplierPayment.useMutation({ onSuccess: onDone });
  const mutation = isSupplier ? supplierMutation : customerMutation;

  const canSubmit = numericAmount > 0 && !!effectiveCurrency && !!partyId && allocationsValid && !mutation.isPending;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    const common = {
      amount: numericAmount,
      currency: effectiveCurrency,
      paymentMethod: method,
      paymentDate: new Date(paymentDate),
      reference: reference || undefined,
      bankAccount: bankAccount || undefined,
      notes: notes || undefined,
      allocations: allocationList,
    };
    if (isSupplier) {
      supplierMutation.mutate({ ...common, supplierId: partyId ?? undefined });
    } else {
      customerMutation.mutate({ ...common, tradingCustomerId: partyId ?? undefined });
    }
  };

  const setAllocation = (id: string, value: string) => {
    setAllocTouched(true);
    setAllocations({ ...effectiveAllocations, [id]: value });
  };

  return (
    <div className="mx-auto max-w-3xl">
      <button onClick={() => navigate(-1)} className="mb-4 inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ArrowLeft className="h-4 w-4" /> {tCommon('actions.back', { defaultValue: 'Back' })}
      </button>

      <div className="rounded-lg border bg-card p-6 shadow-sm">
        <div className="mb-6 flex items-center gap-3">
          <div className="rounded-md bg-primary/10 p-2 text-primary">
            <Banknote className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-lg font-semibold">{isSupplier ? 'Tedarikçi Ödemesi / Supplier Payment' : 'Tahsilat / Customer Payment'}</h1>
            {linkedNumber && (
              <p className="text-sm text-muted-foreground">
                {linkedNumber}
                {balanceDue > 0 && (
                  <>
                    {' · '}
                    {tCommon('balanceDue', { defaultValue: 'Balance due' })}: {formatCurrency(balanceDue, linkedCurrency)}
                  </>
                )}
              </p>
            )}
            {!linkedNumber && !partyId && (
              <p className="text-sm text-red-600">Cari seçilmedi — bir fatura ya da cari kartından gelin. / No counterparty: open this page from an invoice or a cari card.</p>
            )}
          </div>
        </div>

        {loadingLinked ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="amount">Tutar / Amount</Label>
                <Input
                  id="amount"
                  type="number"
                  step="0.01"
                  min="0"
                  value={effectiveAmount}
                  onChange={(e) => {
                    setTouched(true);
                    setAmount(e.target.value);
                  }}
                />
              </div>
              <div>
                <Label htmlFor="currency">Para Birimi / Currency</Label>
                <Input id="currency" value={effectiveCurrency} onChange={(e) => setCurrency(e.target.value.toUpperCase())} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="method">Ödeme Yöntemi / Method</Label>
                <select
                  id="method"
                  value={method}
                  onChange={(e) => setMethod(e.target.value as PaymentMethod)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                >
                  {PAYMENT_METHODS.map((m) => (
                    <option key={m} value={m}>
                      {m.replace('_', ' ')}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <Label htmlFor="date">Tarih / Date</Label>
                <Input id="date" type="date" value={paymentDate} onChange={(e) => setPaymentDate(e.target.value)} />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="reference">Referans / Reference</Label>
                <Input id="reference" value={reference} onChange={(e) => setReference(e.target.value)} />
              </div>
              <div>
                <Label htmlFor="bankAccount">Banka Hesabı / Bank Account</Label>
                <Input id="bankAccount" value={bankAccount} onChange={(e) => setBankAccount(e.target.value)} />
              </div>
            </div>

            {/* Allocation */}
            {partyId && (
              <div className="rounded-md border border-border">
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-3 py-2">
                  <div>
                    <div className="text-sm font-medium">Fatura eşleştirme / Apply to invoices</div>
                    <div className="text-xs text-muted-foreground">
                      Eşleşmeyen tutar cari hesapta avans olarak kalır. / Whatever is not allocated stays on account.
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    disabled={numericAmount <= 0 || sameCurrency.length === 0}
                    onClick={() => {
                      setAllocTouched(true);
                      setAllocations(fifoAllocation(numericAmount, invoices, effectiveCurrency));
                    }}
                  >
                    <ListOrdered className="mr-1 h-3.5 w-3.5" />
                    Vadeye göre dağıt / FIFO
                  </Button>
                </div>
                {openInvoices.isLoading ? (
                  <div className="flex h-16 items-center justify-center">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                  </div>
                ) : sameCurrency.length === 0 ? (
                  <p className="px-3 py-3 text-sm text-muted-foreground">
                    {effectiveCurrency} cinsinden açık fatura yok; tutar avans olarak kaydedilir. / No open {effectiveCurrency} invoices; the amount is recorded on account.
                  </p>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-border text-left text-[11px] uppercase tracking-wide text-muted-foreground">
                        <th className="px-3 py-1.5 font-medium">Fatura / Invoice</th>
                        <th className="px-3 py-1.5 font-medium">Vade / Due</th>
                        <th className="px-3 py-1.5 text-right font-medium">Açık bakiye / Open</th>
                        <th className="px-3 py-1.5 text-right font-medium">Uygulanan / Applied</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sameCurrency.map((inv) => {
                        const value = effectiveAllocations[inv.id] ?? '';
                        const over = Number(value) > inv.balanceDue + 0.005;
                        return (
                          <tr key={inv.id} className="border-b border-border last:border-b-0">
                            <td className="px-3 py-1.5 font-mono text-xs">{inv.invoiceNumber}</td>
                            <td className="px-3 py-1.5 text-xs text-muted-foreground">{inv.dueDate ? formatDate(inv.dueDate) : '—'}</td>
                            <td className="px-3 py-1.5 text-right tabular-nums">{formatCurrency(inv.balanceDue, inv.currency)}</td>
                            <td className="px-3 py-1.5 text-right">
                              <Input
                                type="number"
                                step="0.01"
                                min="0"
                                max={inv.balanceDue}
                                value={value}
                                onChange={(e) => setAllocation(inv.id, e.target.value)}
                                className={`ml-auto h-8 w-[140px] text-right ${over ? 'border-red-500' : ''}`}
                              />
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="text-xs">
                        <td className="px-3 py-2" colSpan={2}>
                          {otherCurrency > 0 && (
                            <span className="text-muted-foreground">
                              {otherCurrency} fatura başka para biriminde (eşleştirilemez) / {otherCurrency} in another currency
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-2 text-right text-muted-foreground">Eşleşen / Allocated</td>
                        <td className="px-3 py-2 text-right tabular-nums font-medium">{formatCurrency(allocatedTotal, effectiveCurrency)}</td>
                      </tr>
                      <tr className="text-xs">
                        <td colSpan={3} className="px-3 py-1 text-right text-muted-foreground">
                          Avans (cari hesapta) / On account
                        </td>
                        <td className={`px-3 py-1 text-right tabular-nums font-medium ${unallocated < -0.005 ? 'text-red-600' : ''}`}>
                          {formatCurrency(unallocated, effectiveCurrency)}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                )}
                {!allocationsValid && (
                  <p className="px-3 py-2 text-xs text-red-600">
                    Eşleştirme tutarı ödeme tutarını veya faturanın açık bakiyesini aşamaz. / Allocations cannot exceed the payment or an invoice's open balance.
                  </p>
                )}
              </div>
            )}

            <div>
              <Label htmlFor="notes">Not / Notes</Label>
              <Textarea id="notes" value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} />
            </div>

            {mutation.error && <p className="text-sm text-red-600">{mutation.error.message}</p>}

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => navigate(-1)}>
                {tCommon('actions.cancel', { defaultValue: 'Cancel' })}
              </Button>
              <Button type="submit" disabled={!canSubmit}>
                {mutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {tCommon('actions.save', { defaultValue: 'Save' })}
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
